// ============================================================
// 4. VoidRefundReportPage.tsx
// ============================================================
// src/features/reports/pages/VoidRefundReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getVoidRefundReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    RotateCcw, AlertTriangle, DollarSign, TrendingDown,
    ShoppingCart, XCircle, RefreshCw, FileText
} from 'lucide-react';

export function VoidRefundReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState<'voided' | 'refunded' | 'reasons'>('voided');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-void-refund', tenantId, params],
        queryFn: () => getVoidRefundReport(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'void_refund', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30">
            <ReportHeader
                title="Void & Refund Report"
                description="Transaction reversals analysis"
                icon={RotateCcw}
                iconGradient="from-rose-500 to-pink-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<RotateCcw className="w-10 h-10" />}
                        title="No Void/Refund Data"
                        description="No void or refund transactions found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Voids"
                                value={String(data.summary?.totalVoided || 0)}
                                icon={XCircle}
                                gradient="from-rose-500 to-pink-600"
                            />
                            <SummaryCard
                                label="Total Refunds"
                                value={String(data.summary?.totalRefunded || 0)}
                                icon={RefreshCw}
                                gradient="from-amber-500 to-orange-600"
                            />
                            <SummaryCard
                                label="Void Amount"
                                value={formatNaira(data.summary?.totalVoidAmount || 0)}
                                icon={TrendingDown}
                                gradient="from-red-500 to-rose-600"
                            />
                            <SummaryCard
                                label="Refund Amount"
                                value={formatNaira(data.summary?.totalRefundAmount || 0)}
                                icon={DollarSign}
                                gradient="from-orange-500 to-yellow-600"
                            />
                        </div>

                        {/* Tab Navigation */}
                        <div className="flex gap-2 border-b border-slate-200 pb-2">
                            {(['voided', 'refunded', 'reasons'] as const).map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors capitalize ${activeTab === tab
                                            ? 'bg-rose-50 text-rose-700'
                                            : 'text-slate-500 hover:bg-slate-50'
                                        }`}
                                >
                                    {tab === 'reasons' ? 'Void Reasons' : tab}
                                </button>
                            ))}
                        </div>

                        {activeTab === 'voided' && (
                            <Card className="overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                    <h3 className="text-sm font-semibold text-slate-800">Voided Transactions</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Order #</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Cashier</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Reason</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Items</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {data.voided?.map((v: any, i: number) => (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                        {v.orderNumber}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{v.cashierName}</td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-rose-600">
                                                        {formatNaira(v.grandTotal)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm">
                                                        <Badge variant="danger" size="sm">
                                                            {v.reason || 'No reason'}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        {v.items?.map((item: any, j: number) => (
                                                            <span key={j} className="block text-xs">
                                                                {item.name} x{item.qty}
                                                            </span>
                                                        ))}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        {v.voidedAt ? new Date(v.voidedAt).toLocaleDateString() : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}

                        {activeTab === 'refunded' && (
                            <Card className="overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                    <h3 className="text-sm font-semibold text-slate-800">Refunded Transactions</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Order #</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Cashier</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {data.refunded?.map((r: any, i: number) => (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                        {r.orderNumber}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{r.cashierName}</td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-amber-600">
                                                        {formatNaira(r.grandTotal)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        {new Date(r.createdAt).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}

                        {activeTab === 'reasons' && data.voidReasons && (
                            <Card className="overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                    <h3 className="text-sm font-semibold text-slate-800">Void Reason Breakdown</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Reason</th>
                                                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Count</th>
                                                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">% of Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {data.voidReasons.map((reason: any, i: number) => {
                                                const total = data.summary?.totalVoided || 1;
                                                const percentage = ((reason.count / total) * 100).toFixed(1);
                                                return (
                                                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                        <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                            {reason.reason}
                                                        </td>
                                                        <td className="px-6 py-4 text-center text-sm font-semibold">
                                                            {reason.count}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <div className="flex items-center gap-2">
                                                                <div className="flex-1 bg-slate-100 rounded-full h-2">
                                                                    <div
                                                                        className="h-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-600"
                                                                        style={{ width: `${percentage}%` }}
                                                                    />
                                                                </div>
                                                                <span className="text-xs text-slate-500 w-12">{percentage}%</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}